import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import { QueryStatus } from '@reduxjs/toolkit/query';
import { formatDistanceStrict } from 'date-fns';
import React from 'react';
import { useSelector } from 'react-redux';

import { customerSelector, useCreateFakePIXPaymentMutation } from '@/api/v1';
import Copy from '@/assets/icons/copy.svg?react';
import { LoadingButton } from '@/components/molecules/LoadingButton';
import { PIXQRCode } from '@/components/molecules/PIXQRCode';
import { SHOULD_MOCK_DEPOSITS } from '@/constants';
import { useToast } from '@/hooks/useToast';
import { LocaleContext, useTranslator } from '@/hooks/useTranslator';
import { getCustomerTaxId } from '@/types/customer';
import { Locale } from '@/types/locale';
import { toDateFnsLocale } from '@/utility/toDateFnsLocale';

interface Props {
  readonly pixCopyAndPaste: string;
  readonly expiresAt: string;
  readonly transactionId: string;
}

export const PixQRCodeSection: React.FC<Props> = (props: Props): React.ReactElement => {
  const { pixCopyAndPaste, expiresAt, transactionId } = props;
  const toast = useToast();
  const tr = useTranslator();
  const locale = React.useContext<Locale>(LocaleContext);

  const { data: customer } = useSelector(customerSelector);
  const [createFakePIXTransaction, mutationResult] = useCreateFakePIXPaymentMutation();
  const [expanded, setExpanded] = React.useState<boolean>(false);
  const [isCopying, setIsCopying] = React.useState<boolean>(false);

  React.useEffect((): void => {
    switch (mutationResult.status) {
      case QueryStatus.rejected:
        toast.show(tr('AN_ERROR_OCCURRED'), 'error');
        break;
      default:
        break;
    }
  }, [mutationResult.status, toast, tr]);

  const copyToClipboard = React.useCallback(async (): Promise<void> => {
    const { clipboard } = navigator;

    setIsCopying(true);
    try {
      await clipboard.writeText(pixCopyAndPaste);
      if (SHOULD_MOCK_DEPOSITS) {
        const taxId = getCustomerTaxId(customer);

        console.debug(`creating fake pix transaction for ${taxId}`);
        createFakePIXTransaction({
          type: 'PIX',
          transactionId: transactionId,
          documentId: taxId,
        });
      }
      toast.show(tr('LINK_COPIED'), 'success');
    } catch {
      toast.show(tr('AN_ERROR_OCCURRED'), 'error');
    } finally {
      setIsCopying(false);
    }
  }, [pixCopyAndPaste, toast, tr, customer, createFakePIXTransaction, transactionId]);

  const visiblePixString = React.useMemo((): string => {
    if (expanded) {
      return pixCopyAndPaste;
    } else if (pixCopyAndPaste.length > 70) {
      return pixCopyAndPaste.slice(0, 70) + '\u2026';
    } else {
      return pixCopyAndPaste;
    }
  }, [expanded, pixCopyAndPaste]);

  const expand = React.useCallback((): void => {
    setExpanded(true);
  }, []);

  const expirationFormatted = React.useMemo((): string | null => {
    const expirationDate = new Date(expiresAt);

    if (isNaN(expirationDate.getTime())) {
      return null;
    }

    if (expirationDate.getTime() <= Date.now()) {
      return null;
    }

    return formatDistanceStrict(expirationDate, new Date(), {
      unit: 'hour',
      roundingMethod: 'ceil',
      locale: toDateFnsLocale(locale),
    });
  }, [locale, expiresAt]);

  return (
    <Box display="flex" flexDirection="column" gap={3} py={2}>
      <Box sx={styles.codeContainer}>
        <Box sx={styles.codeQR}>
          <PIXQRCode content={pixCopyAndPaste} />
        </Box>
        <Box sx={styles.codeStringAndButtonContainer}>
          <Box sx={styles.codeStringBox}>
            <Typography component="div" fontSize={14} fontWeight={400} lineHeight="24px">
              <Typography
                sx={styles.codeString}
                fontSize="inherit"
                fontWeight="inherit"
                lineHeight="inherit">
                {visiblePixString}
              </Typography>
              {!expanded ? (
                <Typography
                  sx={styles.seeAllLink}
                  component="a"
                  color="custom.LightGray"
                  fontSize="inherit"
                  fontWeight="inherit"
                  lineHeight="inherit"
                  onClick={expand}>
                  {tr('SEE_ALL')}
                </Typography>
              ) : (
                <></>
              )}
            </Typography>
          </Box>
          <LoadingButton
            variant="dark"
            size="compact"
            startIcon={<Copy fill="currentColor" />}
            loading={isCopying}
            onClick={copyToClipboard}>
            {tr('COPY_LINK')}
          </LoadingButton>
        </Box>
      </Box>
      {expirationFormatted && (
        <Box textAlign="center">
          <Typography fontSize={16} fontWeight={400}>
            <Typography fontSize="inherit" fontWeight="inherit" component="span">
              {tr('QUOTE_WILL_EXPIRE')}{' '}
            </Typography>
            <Typography
              fontSize="inherit"
              fontWeight="inherit"
              component="span"
              color="primary.main">
              {expirationFormatted}
            </Typography>
          </Typography>
        </Box>
      )}
    </Box>
  );
};

const styles: Record<string, SystemStyleObject> = {
  codeContainer: {
    display: 'flex',
    alignItems: 'flex-start',
    backgroundColor: 'custom.Primary1Background',
    padding: 4,
    gap: 3,
    borderRadius: 3,
  },
  seeAllLink: {
    cursor: 'pointer',
  },
  codeString: {
    whiteSpace: 'wrap',
    overflowWrap: 'break-word',
    wordBreak: 'break-all',
    overflow: 'hidden',
  },
  codeStringBox: {
    mb: 2,
  },
  codeStringAndButtonContainer: {
    minWidth: 0,
    flex: 1,
  },
  codeQR: {
    backgroundColor: 'primary.contrastText',
    flexShrink: 0,
    padding: 1,
    borderRadius: 2,
    width: 156,
    height: 156,
  },
};
